/**
 * Dreamscape — Time-of-day and seasonal background rotation.
 *
 * Fetches the manifest from the CDN and selects a background image
 * based on the user's local time and the current season.
 * Rotates daily using a date-based seed for consistent daily experience.
 */

import { getAssetUrl } from './assets';

export type TimeOfDay = 'morning' | 'afternoon' | 'evening' | 'night';

export interface DreamscapeImage {
  url: string;
  key: string;
  timeOfDay: string;
  season: string;
  month: string;
  variant: number;
  scene: string;
  generatedAt: string;
}

interface Manifest {
  version: number;
  updatedAt: string;
  images: DreamscapeImage[];
}

// ─── Time of Day ──────────────────────────────────────────────────────────────

export function getTimeOfDay(hour?: number): TimeOfDay {
  const h = hour ?? new Date().getHours();
  if (h >= 6 && h < 12) return 'morning';
  if (h >= 12 && h < 17) return 'afternoon';
  if (h >= 17 && h < 20) return 'evening';
  return 'night';
}

/**
 * Get blend factor between two adjacent time periods for smooth transitions.
 * Returns { current, next, blend } where blend is 0-1.
 * blend=0 means fully current, blend=1 means fully next.
 */
export function getTimeBlend(): {
  current: TimeOfDay;
  next: TimeOfDay;
  blend: number;
} {
  const now = new Date();
  const h = now.getHours();
  const m = now.getMinutes();
  const fractionalHour = h + m / 60;

  // Transition windows (1 hour each)
  const transitions: Array<{
    start: number;
    from: TimeOfDay;
    to: TimeOfDay;
  }> = [
    { start: 5.5, from: 'night', to: 'morning' },
    { start: 11.5, from: 'morning', to: 'afternoon' },
    { start: 16.5, from: 'afternoon', to: 'evening' },
    { start: 19.5, from: 'evening', to: 'night' },
  ];

  for (const t of transitions) {
    if (fractionalHour >= t.start && fractionalHour < t.start + 1) {
      return {
        current: t.from,
        next: t.to,
        blend: fractionalHour - t.start,
      };
    }
  }

  const current = getTimeOfDay(h);
  return { current, next: current, blend: 0 };
}

// ─── Season ───────────────────────────────────────────────────────────────────

export function getSeason(month?: number): string {
  const m = month ?? new Date().getMonth() + 1;
  if (m >= 3 && m <= 5) return 'spring';
  if (m >= 6 && m <= 8) return 'summer';
  if (m >= 9 && m <= 11) return 'autumn';
  return 'winter';
}

// ─── Daily Seed ───────────────────────────────────────────────────────────────

/** Simple hash for deterministic daily rotation */
function dailySeed(): number {
  const today = new Date();
  const dateStr = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
  let hash = 0;
  for (let i = 0; i < dateStr.length; i++) {
    const char = dateStr.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return Math.abs(hash);
}

// ─── Literary Quotes ──────────────────────────────────────────────────────────

const LITERARY_QUOTES = [
  { text: 'There is no charm equal to tenderness of heart.', author: 'Jane Austen' },
  {
    text: 'Tell me, what is it you plan to do with your one wild and precious life?',
    author: 'Mary Oliver',
  },
  { text: 'We loved with a love that was more than love.', author: 'Edgar Allan Poe' },
  {
    text: 'I am not afraid of storms, for I am learning how to sail my ship.',
    author: 'Louisa May Alcott',
  },
  { text: 'The soul that sees beauty may sometimes walk alone.', author: 'Goethe' },
  {
    text: 'She was beautiful, but not like those girls in the magazines. She was beautiful, for the way she thought.',
    author: 'F. Scott Fitzgerald',
  },
  {
    text: 'I have loved the stars too fondly to be fearful of the night.',
    author: 'Sarah Williams',
  },
  {
    text: 'In the middle of winter, I at last discovered that there was in me an invincible summer.',
    author: 'Albert Camus',
  },
  { text: 'Whatever our souls are made of, his and mine are the same.', author: 'Emily Brontë' },
  { text: 'The wound is the place where the Light enters you.', author: 'Rumi' },
  { text: 'I dwell in Possibility.', author: 'Emily Dickinson' },
  {
    text: 'And the day came when the risk to remain tight in a bud was more painful than the risk it took to blossom.',
    author: 'Anaïs Nin',
  },
  {
    text: 'She was a wild one; always stomping on eggshells that everyone else tiptoed on.',
    author: 'Kaitlin Foster',
  },
  { text: 'In a gentle way, you can shake the world.', author: 'Mahatma Gandhi' },
  {
    text: 'The most beautiful things in the world cannot be seen or even touched. They must be felt with the heart.',
    author: 'Helen Keller',
  },
  { text: 'Light tomorrow with today.', author: 'Elizabeth Barrett Browning' },
  { text: 'To love and be loved is to feel the sun from both sides.', author: 'David Viscott' },
  {
    text: 'One must still have chaos in oneself to be able to give birth to a dancing star.',
    author: 'Nietzsche',
  },
  {
    text: 'Perhaps all the dragons in our lives are princesses who are only waiting to see us act, just once, with beauty and courage.',
    author: 'Rilke',
  },
  {
    text: 'You yourself, as much as anybody in the entire universe, deserve your love and affection.',
    author: 'Buddha',
  },
];

/**
 * Returns a curated literary quote that rotates daily using a date-based seed.
 */
export function getDailyQuote(): { text: string; author: string } {
  const seed = dailySeed();
  return LITERARY_QUOTES[seed % LITERARY_QUOTES.length];
}

// ─── Manifest & Selection ─────────────────────────────────────────────────────

let cachedManifest: Manifest | null = null;
let manifestFetchedAt = 0;
const MANIFEST_CACHE_MS = 60 * 60 * 1000; // 1 hour

export async function fetchManifest(): Promise<Manifest | null> {
  const now = Date.now();
  if (cachedManifest && now - manifestFetchedAt < MANIFEST_CACHE_MS) {
    return cachedManifest;
  }

  try {
    const url = getAssetUrl('/backgrounds/manifest.json');
    const response = await fetch(`${url}?t=${Math.floor(now / MANIFEST_CACHE_MS)}`);
    if (!response.ok) return cachedManifest;
    cachedManifest = await response.json();
    manifestFetchedAt = now;
    return cachedManifest;
  } catch {
    return cachedManifest;
  }
}

/**
 * Select a background image for the current time and season.
 * Uses a daily seed so the image stays consistent throughout the day
 * but changes each day.
 */
export function selectBackground(manifest: Manifest, timeOfDay: TimeOfDay): DreamscapeImage | null {
  const season = getSeason();

  // Filter: matching time of day AND current season
  let candidates = manifest.images.filter(
    (img) => img.timeOfDay === timeOfDay && img.season === season,
  );

  // Fallback: any image for this time of day (in case no seasonal match)
  if (candidates.length === 0) {
    candidates = manifest.images.filter((img) => img.timeOfDay === timeOfDay);
  }

  if (candidates.length === 0) return null;

  // Pick deterministically by daily seed
  const seed = dailySeed();
  const index = seed % candidates.length;
  return candidates[index];
}

/**
 * Get the current and (optionally) next background for smooth transitions.
 */
export async function getCurrentBackgrounds(): Promise<{
  current: DreamscapeImage | null;
  next: DreamscapeImage | null;
  blend: number;
}> {
  const manifest = await fetchManifest();
  if (!manifest || manifest.images.length === 0) {
    return { current: null, next: null, blend: 0 };
  }

  const { current: currentTime, next: nextTime, blend } = getTimeBlend();
  const currentBg = selectBackground(manifest, currentTime);
  const nextBg = blend > 0 ? selectBackground(manifest, nextTime) : null;

  return { current: currentBg, next: nextBg, blend };
}
